// Client-side metadata for the PAD Info module. Keep the enums and the detail sentences in
// sync with Server/src/interfaces/pad-info/pad-info.interface.ts and
// Server/src/services/pad-info/pad-status.ts.
//
// A PAD entry records WHAT changed and WHEN — never money, never banking details. The rent
// is always read back from the mastersheet (see totalRentFromItem below).

export const PAD_CATEGORIES = [
  {
    value: "NEW",
    label: "New PAD",
    short: "New",
    color: "#2E7D32",
    bg: "#E8F5E9",
    icon: "plus-circle",
    blurb: "A new resident is moving in and has set up a PAD.",
    dateLabel: "Resident’s first pull date",
    fields: [],
  },
  {
    value: "TRANSFER",
    label: "Transfer PAD",
    short: "Transfer",
    color: "#00838F",
    bg: "#E0F7FA",
    icon: "shuffle",
    blurb: "The resident's existing PAD carries over — no paperwork needed.",
    dateLabel: "Resident’s first pull date",
    fields: ["transfer_reason"],
  },
  {
    value: "CHANGE",
    label: "PAD Change",
    short: "Change",
    color: "#1565C0",
    bg: "#E3F2FD",
    icon: "refresh-cw",
    blurb: "An existing resident changed their banking information.",
    dateLabel: "Effective from",
    fields: [],
  },
  {
    value: "AMOUNT_CHANGE",
    label: "New Rent Amount",
    short: "Amount",
    color: "#6A1B9A",
    bg: "#F3E5F5",
    icon: "dollar-sign",
    blurb: "Rent amount change.",
    dateLabel: "Effective from",
    fields: [],
  },
  {
    value: "DELAY",
    label: "PAD Delay",
    short: "Delay",
    color: "#EF6C00",
    bg: "#FFF3E0",
    icon: "clock",
    blurb: "The pull happens on a later date than usual (e.g. the 10th, not the 1st).",
    dateLabel: "PAD delay date",
    fields: ["delay_resume"],
  },
  {
    value: "SPLIT",
    label: "Split PAD",
    short: "Split",
    color: "#AD1457",
    bg: "#FCE4EC",
    icon: "layers",
    blurb: "The rent is pulled in parts on multiple dates each month — the parts must add up to the rent.",
    dateLabel: "First pull date",
    fields: ["split_pulls"],
  },
  {
    value: "TEMP_STOP",
    label: "Temporary Stop",
    short: "Temp stop",
    color: "#8D6E63",
    bg: "#EFEBE9",
    icon: "pause-circle",
    blurb: "The resident is temporarily paying another way (e-transfer, cheque…).",
    dateLabel: "Pause from",
    fields: ["temp_resume", "temp_method"],
  },
  {
    value: "STOP",
    label: "PAD Stop",
    short: "Stop",
    color: "#C62828",
    bg: "#FFEBEE",
    icon: "x-circle",
    blurb: "The resident is moving out or the PAD is cancelled.",
    dateLabel: "PAD stops from",
    // Move-out date removed from the form (owner, 2026-08-28) — the stop date says it all.
    // Old entries that carry one still show it in their sentences.
    fields: ["stop_reason"],
  },
];

export const categoryMeta = (value) => PAD_CATEGORIES.find((c) => c.value === value) || PAD_CATEGORIES[0];

export const ALTERNATE_METHODS = ["E-Transfer", "Cheque", "Cash", "Credit Card", "Other"];

export const STOP_REASONS = [
  { value: "MOVING_OUT", label: "Moving out" },
  { value: "BOUGHT_HOUSE", label: "Bought house" },
  { value: "LEASE_BREAK", label: "Lease break" },
  { value: "NOT_RENEWING", label: "Not renewing" },
  { value: "EVICTION", label: "Eviction" },
  { value: "SWITCH_PAYMENT_METHOD", label: "Switching payment method" },
  { value: "OTHER", label: "Other" },
];
const STOP_REASON_LABEL = Object.fromEntries(STOP_REASONS.map((r) => [r.value, r.label]));

/**
 * Mastersheet fee columns that make up the monthly rent, in display order — the same set a
 * lease addendum amends (Addendum/AddendumModal.jsx MONTHLY_CHARGE_KEYS). `aliases` are the
 * alternate storage keys some property layouts use. Mirrors MONTHLY_CHARGE_KEYS in
 * Server/src/services/pad-info/pad-status.ts.
 */
export const MONTHLY_CHARGE_KEYS = [
  { key: "basicRent", label: "Rent" },
  { key: "ssFee", label: "SS Parking", aliases: ["surfaceStallFee"] },
  { key: "ugFee", label: "UG Parking", aliases: ["undergroundFee"] },
  { key: "dogFee", label: "Dog Fee" },
  { key: "catFee", label: "Cat Fee" },
  { key: "otherPetFee", label: "Other Pet Fee" },
  { key: "storageFee", label: "Storage Fee" },
  { key: "otherFee", label: "Other Fee" },
  // Monthly AC charge (The Vibe) — owner-approved 2026-08-28 as part of the rent total.
  { key: "acFee", label: "AC Charges" },
  { key: "telus", label: "Telus", aliases: ["telusFee"] },
  { key: "extraTenantFee", label: "Extra Tenant Fee" },
];

export const PAD_STATE_META = {
  ON_PAD: { label: "On PAD", color: "#0F6E56", bg: "#E1F5EE" },
  STOPPED: { label: "PAD stopped", color: "#A32D2D", bg: "#FCEBEB" },
  TEMP_STOPPED: { label: "Temporarily stopped", color: "#854F0B", bg: "#FAEEDA" },
  NO_PAD: { label: "No PAD info", color: "#A32D2D", bg: "#FCEBEB" },
  VACANT: { label: "Vacant", color: "#5F5E5A", bg: "#EEF0F2" },
};

export const SOURCE_META = {
  DATA: { label: "Data in Rentdigicare", color: "#1B5E20", bg: "#E2F0D9", rowClass: "table-success" },
  DOCUMENT: { label: "Document only", color: "#7A5A00", bg: "#FFF2CC", rowClass: "table-warning" },
  NONE: { label: "Nothing available", color: "#8B1A1A", bg: "#F8D7DA", rowClass: "table-danger" },
};

const num = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/[^0-9.-]/g, ""));
  return isNaN(n) ? 0 : n;
};
export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Total monthly rent for a unit, summed straight from its mastersheet fee columns.
 *
 * The mastersheet is the single source of truth for money — PAD never stores an amount of its
 * own, so this is recomputed every render. `targetRent` is a goal, not a charge, and is
 * excluded. Returns null when the row carries no rent columns at all.
 */
export function totalRentFromItem(item) {
  if (!item) return null;
  let total = 0;
  let found = false;
  for (const { key, aliases } of MONTHLY_CHARGE_KEYS) {
    for (const k of [key, ...(aliases || [])]) {
      const v = item[k];
      if (v === null || v === undefined || v === "") continue;
      const n = typeof v === "number" ? v : parseFloat(String(v).replace(/[$,\s]/g, ""));
      if (!isFinite(n)) continue;
      total += n;
      found = true;
      break; // first key that carries a value wins; aliases are alternates, not extras
    }
  }
  return found ? round2(total) : null;
}

/** The non-zero fee columns behind totalRentFromItem — for a read-only breakdown. */
export function rentBreakdownFromItem(item) {
  if (!item) return [];
  const rows = [];
  for (const { key, label, aliases } of MONTHLY_CHARGE_KEYS) {
    for (const k of [key, ...(aliases || [])]) {
      const v = item[k];
      if (v === null || v === undefined || v === "") continue;
      const n = typeof v === "number" ? v : parseFloat(String(v).replace(/[$,\s]/g, ""));
      if (!isFinite(n)) continue;
      if (n !== 0) rows.push({ key, label, amount: round2(n) });
      break;
    }
  }
  return rows;
}

export const money = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = num(v);
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** 1 → "1st", 22 → "22nd", 13 → "13th" — how a split's monthly days read. */
const dayOrdinal = (day) => {
  const mod100 = day % 100;
  const suffix = mod100 >= 11 && mod100 <= 13 ? "th" : { 1: "st", 2: "nd", 3: "rd" }[day % 10] || "th";
  return `${day}${suffix}`;
};

/** "$700.00 on the 1st + $800.00 on the 15th" — a split schedule as one phrase (mirrors the server). */
export function formatSplitSchedule(pulls) {
  return (pulls || [])
    .map((p) => {
      const day = parseInt(String(p?.date || "").slice(8, 10), 10);
      return `${money(Number(p?.amount) || 0)} on the ${Number.isFinite(day) ? dayOrdinal(day) : "—"}`;
    })
    .join(" + ");
}

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const formatMonthLabel = (month) => {
  const m = /^(\d{4})-(\d{2})/.exec(String(month || ""));
  if (!m) return "";
  return `${MONTHS_SHORT[parseInt(m[2], 10) - 1] || m[2]} ${m[1]}`;
};
export const monthOf = (ymd) => (/^\d{4}-\d{2}/.test(String(ymd || "")) ? String(ymd).slice(0, 7) : "");

/** Same sentence the server prints in the report — one line describing the entry. */
export function formatEntryDetails(e, opts = {}) {
  if (!e) return "";
  const parts = [];
  switch (e.category) {
    case "NEW": {
      parts.push(`First pull ${e.effective_date || "—"}`);
      // The pull was moved off the lease start — say from where, and why.
      const np = e.new_pad || {};
      if (np.reason) {
        parts.push(np.lease_start_date ? `moved from lease start ${np.lease_start_date}` : "moved from lease start");
        parts.push(String(np.reason));
      }
      break;
    }
    case "TRANSFER": {
      // A New PAD without paperwork. The reason is part of the record wherever it was given.
      parts.push(`PAD transferred — first pull ${e.effective_date || "—"}`);
      if (e.transfer?.reason) parts.push(String(e.transfer.reason));
      break;
    }
    case "CHANGE": {
      parts.push("Banking changed");
      break;
    }
    case "AMOUNT_CHANGE": {
      // The amount is never on the entry: the server derives it from the addendum (e.derived),
      // and a caller may pass it explicitly.
      const d = e.derived || {};
      const rent = opts.rent ?? d.rent ?? null;
      const prev = opts.previous_rent ?? d.previous_rent ?? null;
      const ref = opts.addendum_ref || d.addendum_ref || e.addendum_ref || "";
      if (rent != null) {
        parts.push(`New amount ${money(rent)} from ${e.effective_date || "—"}`);
        if (prev != null && prev !== rent) parts.push(`was ${money(prev)}`);
      } else {
        parts.push(`New amount from ${e.effective_date || "—"}`);
      }
      parts.push(ref ? `addendum ${ref}` : "see the lease / addendum");
      break;
    }
    case "DELAY": {
      const resume = e.delay?.resume_date;
      parts.push(`Delayed pull from ${e.effective_date || "—"}`);
      if (Number(e.delay?.charge) > 0) parts.push(`${money(Number(e.delay.charge))} delay charge`);
      parts.push(resume ? `regular pulls resume ${resume}` : "same delayed date for the upcoming months");
      break;
    }
    case "SPLIT": {
      const pulls = (e.split && e.split.pulls) || [];
      parts.push(`Split pull ${formatSplitSchedule(pulls)} each month`);
      parts.push(`from ${e.effective_date || "—"}`);
      // A blank resume date is N/A on purpose — the split repeats until replaced.
      if (e.split?.resume_date) parts.push(`regular pulls resume ${e.split.resume_date}`);
      if (e.split?.approved_by) parts.push(`approved by ${String(e.split.approved_by)}`);
      // The pulls added up to the rent when recorded; if the rent has moved since, say so —
      // accounting must not quietly keep pulling the old total. Same sentence as the server.
      const total = round2(pulls.reduce((s, p) => s + (Number(p?.amount) || 0), 0));
      const rent = opts.rent ?? (e.derived || {}).rent ?? null;
      if (rent != null && Math.abs(total - rent) > 0.005) {
        parts.push(`⚠ pulls total ${money(total)} but the rent is ${money(rent)} — update the split`);
      }
      break;
    }
    case "TEMP_STOP": {
      const method = e.temp_stop?.alternate_method;
      const resume = e.temp_stop?.resume_date;
      parts.push(method ? `${method} meanwhile` : "Paying another way meanwhile");
      parts.push(resume ? `resumes ${resume}` : "until further notice");
      break;
    }
    case "STOP": {
      const reason = e.stop?.reason;
      parts.push(reason ? STOP_REASON_LABEL[reason] || reason : "PAD stopped");
      if (e.stop?.move_out_date) parts.push(`move-out ${e.stop.move_out_date}`);
      if (opts.rerented) parts.push("re-rented");
      break;
    }
    default:
      parts.push(categoryMeta(e.category).label);
  }
  return parts.filter(Boolean).join(" · ");
}

/**
 * The unit's expected move-out date, taken from the mastersheet's lease end column. Used to
 * pre-fill a PAD Stop — the person recording it can still overwrite it, because a lease break
 * or an eviction ends the tenancy on a different day than the lease says.
 */
export function moveOutDateFromItem(item) {
  if (!item) return "";
  for (const key of ["leaseEndDmy", "lease_end", "leaseEnd", "moveOutDate"]) {
    const raw = item[key];
    if (!raw) continue;
    if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const d = raw instanceof Date ? raw : new Date(String(raw));
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  return "";
}

/**
 * The unit's lease start date from the mastersheet row. A New PAD's first pull is pre-filled
 * from it (the incoming signed lease wins when there is one) — moving the pull off this date
 * requires a stated reason, e.g. an incentive for the first month.
 */
export function leaseStartDateFromItem(item) {
  if (!item) return "";
  for (const key of ["leaseStartDmy", "leaseStartDMY", "lease_start", "leaseStart", "moveInDate"]) {
    const raw = item[key];
    if (!raw) continue;
    if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const d = raw instanceof Date ? raw : new Date(String(raw));
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  return "";
}

/** Today as "YYYY-MM-DD" in the browser's local calendar. */
export const todayYmd = (now = new Date()) => now.toLocaleDateString("en-CA", { year: "numeric", month: "2-digit", day: "2-digit" });

/** An entry counts from its effective date; before that it is scheduled (mirrors the server's isEntryActive). */
export const isScheduled = (e, today = todayYmd()) => !!e?.effective_date && String(e.effective_date) > today;

/** "YYYY-MM" for next calendar month — accounting prepares the bank file before the 1st. */
export function nextMonthYm(now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth() + 1; // 1..12
  const ny = m === 12 ? y + 1 : y;
  const nm = m === 12 ? 1 : m + 1;
  return `${ny}-${String(nm).padStart(2, "0")}`;
}
